import { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import type { UseQueryResult } from '@tanstack/react-query';

import { ErrorNotice, PanelCard, SkeletonLines, StatRow } from './primitives';
import { CHAINS, formatDifficulty, formatHashrate, type ChainId, type HashrateResponse } from '@/lib/mempool';

export function HashrateCard({
  chainId,
  query,
}: {
  chainId: ChainId;
  query: UseQueryResult<HashrateResponse>;
}) {
  const chain = CHAINS[chainId];
  const { data, isPending, isError, error } = query;

  const points = useMemo(
    () =>
      (data?.hashrates ?? []).map((p) => ({
        ts: p.timestamp,
        eh: p.avgHashrate / 1e18,
      })),
    [data],
  );

  return (
    <PanelCard
      icon={<Activity className={`size-4 ${chain.classes.text}`} />}
      title="Hashrate"
      action={
        data ? (
          <span className="font-mono text-xs text-muted-foreground">30d trend</span>
        ) : undefined
      }
    >
      {isPending ? (
        <SkeletonLines rows={4} />
      ) : isError ? (
        <ErrorNotice message={error.message} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className={`font-mono text-3xl font-semibold tracking-tight ${chain.classes.text}`}>
              {formatHashrate(data.currentHashrate)}
            </span>
            <span className="text-sm text-muted-foreground">current network estimate</span>
          </div>

          {points.length > 1 && (
            <div className="h-24 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id={chain.classes.sparklineId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chain.hex} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={chain.hex} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const point = payload[0].payload as { ts: number; eh: number };
                      return (
                        <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
                          <div className="font-mono font-medium">{point.eh.toFixed(0)} EH/s</div>
                          <div className="text-muted-foreground">
                            {new Date(point.ts * 1000).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="eh"
                    stroke={chain.hex}
                    strokeWidth={2}
                    fill={`url(#${chain.classes.sparklineId})`}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <StatRow label="Current difficulty" value={formatDifficulty(data.currentDifficulty)} />
        </div>
      )}
    </PanelCard>
  );
}
